import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HiLockClosed, HiGlobeEuropeAfrica, HiClock } from 'react-icons/hi2';
import { useTurnstile } from '@/hooks/useTurnstile';
import {
  Section,
  Content,
  Title,
  Subtitle,
  EmailForm,
  EmailInput,
  NameInput,
  FirmInput,
  SubmitButton,
  Microcopy,
  SuccessMessage,
  ErrorMessage,
  FieldError,
  TurnstileContainer,
  TrustSignals,
  TrustItem,
  MockupWrapper,
  MockupLogo,
  MockupLabel,
  SignupCta,
} from './Hero.styled';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Self-serve signup is gated behind a build-time flag (default OFF).
const SIGNUP_ENABLED = import.meta.env.VITE_SIGNUP_ENABLED === 'true';
const SIGNUP_URL = 'https://app.scribia.pl/signup';

export default function Hero() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [emailError, setEmailError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const { token, enabled, widgetRef, reset } = useTurnstile();

  const validateEmail = (value: string): string => {
    if (!value.trim()) return t('hero.errors.emailRequired');
    if (!EMAIL_RE.test(value.trim())) return t('hero.errors.emailInvalid');
    return '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const emailErr = validateEmail(email);
    setEmailError(emailErr);
    if (emailErr) return;
    if (enabled && !token) {
      setError(t('hero.errors.captcha'));
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch('/api/waitlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          name: name.trim() || undefined,
          company: company.trim() || undefined,
          turnstileToken: token || undefined,
        }),
      });
      const data = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string };
      if (!res.ok || !data.ok) {
        if (data.error === 'invalid_email') setEmailError(t('hero.errors.emailInvalid'));
        else if (data.error === 'captcha_failed') {
          setError(t('hero.errors.captcha'));
          reset();
        } else setError(t('hero.errorMessage'));
        return;
      }
      setSubmitted(true);
    } catch {
      setError(t('hero.errorMessage'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Section id="hero">
      <Content>
        <Title>{t('hero.title')}</Title>
        <Subtitle>{t('hero.subtitle')}</Subtitle>
        {SIGNUP_ENABLED ? (
          <SignupCta href={SIGNUP_URL}>{t('nav.tryFree')}</SignupCta>
        ) : (
          <SignupCta href="#cennik">{t('hero.viewPricing')}</SignupCta>
        )}
        {submitted ? (
          <SuccessMessage>{t('hero.successMessage')}</SuccessMessage>
        ) : (
          <>
            <EmailForm onSubmit={handleSubmit} noValidate>
              <NameInput
                type="text"
                placeholder={t('hero.namePlaceholder')}
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="name"
              />
              <FirmInput
                type="text"
                placeholder={t('hero.firmPlaceholder')}
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                autoComplete="organization"
              />
              <EmailInput
                type="email"
                placeholder={t('hero.emailPlaceholder')}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (emailError) setEmailError(validateEmail(e.target.value));
                }}
                autoComplete="email"
                required
              />
              {emailError && <FieldError>{emailError}</FieldError>}
              {enabled && <TurnstileContainer ref={widgetRef as React.Ref<HTMLDivElement>} />}
              <SubmitButton type="submit" disabled={submitting || (enabled && !token)}>
                {submitting ? t('hero.submitting') : t('hero.cta')}
              </SubmitButton>
            </EmailForm>
            {error && <ErrorMessage>{error}</ErrorMessage>}
            <Microcopy>{t('hero.microcopy')}</Microcopy>
          </>
        )}
        <TrustSignals>
          <TrustItem><HiLockClosed size={14} />{t('hero.trust.encrypted')}</TrustItem>
          <TrustItem><HiGlobeEuropeAfrica size={14} />{t('hero.trust.gdpr')}</TrustItem>
          <TrustItem><HiClock size={14} />{t('hero.trust.setup')}</TrustItem>
        </TrustSignals>
      </Content>
      <MockupWrapper>
        <MockupLogo src="/logo_350_350.png" alt="Scribia" />
        <MockupLabel>Scribia</MockupLabel>
      </MockupWrapper>
    </Section>
  );
}
